<template>
  <div class="lesson-text-box relative">
    <!-- <pre class="text-black">{{ fields.contentGroup?.shadow }}</pre> -->
    <VLessonPlaceholder v-if="isEmptyBlock" />
    
    <!-- Показуємо контент якщо блок не пустий -->
    <div v-else
      class="w-full h-full m-0 box-border transition-all relative"
      :style="[
        CssStyleHelpers.background(
          fields.mainGroup?.color,
          fields.mainGroup?.isShowColor
        ),
        CssStyleHelpers.borderRadius(fields.mainGroup?.borderRadius),
        CssStyleHelpers.margins(fields.mainGroup?.margins),
        CssStyleHelpers.paddings(fields.mainGroup?.paddings),
      ]"
    >
      <!-- Медиа контент как фон -->
      <div v-if="fields.mainGroup?.isShowMedia" class="media-background">
        <MediaCard
          fill
          :media-type="fields.mainGroup?.type"
          :image-props="fields.mainGroup?.imageProps"
          :video-props="fields.mainGroup?.videoProps"
          :mask-props="fields.mainGroup?.maskProps"
          :object-fit="fields.mainGroup?.objectFit || 'cover'"
        />
      </div>

      <!-- Паттерн як фон через псевдоелемент -->
      <div 
        v-if="fields.mainGroup?.isShowPattern" 
        class="pattern-background absolute inset-0 pointer-events-none"
        :style="StyleUtils.pattern(fields.mainGroup?.pattern, fields.mainGroup?.isShowPattern)"
      ></div>

      <!-- Текст поверх медиа -->
      <div
        class="lesson-text-overlay text-black"
        :key="`${fields.contentGroup?.blur}-${fields.contentGroup?.borderRadius}-${fields.contentGroup?.isShowOverlay}`"
        :style="[
          CssStyleHelpers.blur(
            fields.contentGroup?.blur,
            fields.contentGroup?.isShowOverlay
          ),
          CssStyleHelpers.borderRadius(fields.contentGroup?.borderRadius),
          CssStyleHelpers.paddings(fields.contentGroup?.paddings),
          CssStyleHelpers.background(
            fields.contentGroup?.backgroundColor,
            fields.contentGroup?.isShowOverlay
          ),
          CssStyleHelpers.filterShadow(fields.contentGroup?.shadow),
        ]"
      >
        <TextCard
          v-if="fields.titleGroup?.isShowTitle"
          class="lesson-title"
          :style="[
            CssStyleHelpers.margins({
              bottom: fields.titleGroup?.spacing,
            }),
          ]"
          :text="fields.titleGroup?.title"
        ></TextCard>
        <TextCard :text="fields.contentGroup?.text" ></TextCard>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, PropType } from "vue";
import { useDataFields } from "../composables/data";
import MediaCard from "../cards/MediaCard.vue";
import VLessonPlaceholder from "../VLessonPlaceholder.vue";
import { ILayoutComponentData } from "../types/ILayoutFieldData";
import {
  CssImageHelpers,
  CssStyleHelpers,
  CssVideoHelpers,
  StyleUtils,
} from "../utils/cssStyleHelpers";
import TextCard from "../cards/TextCard.vue";

const props = defineProps({
  modelValue: {
    type: Object as PropType<ILayoutComponentData | undefined>,
    required: true,
  },
  mode: {
    type: String as PropType<"view" | "edit">,
    default: "view",
  },
});

// Використовуємо дані напряму з newData
const applyDefaultMainPaddings = (paddings?: Record<string, number>) => ({
  top: paddings?.top ?? 0,
  right: paddings?.right ?? 0,
  bottom: paddings?.bottom ?? 0,
  left: paddings?.left ?? 0,
  ...paddings,
});

const applyDefaultContentPaddings = (paddings?: Record<string, number>) => ({
  top: paddings?.top ?? 0,
  right: paddings?.right ?? 0,
  bottom: paddings?.bottom ?? 0,
  left: paddings?.left ?? 0,
  ...paddings,
});

const applyDefaultMainMargins = (margins?: Record<string, number>) => ({
  top: margins?.top ?? 50,
  right: margins?.right ?? 0,
  bottom: margins?.bottom ?? 0,
  left: margins?.left ?? 0,
  ...margins,
});

const fields = computed(() => {
  const newData = props.modelValue?.data;
  if (!newData)
    return {
      titleGroup: {},
      contentGroup: {},
      overlayGroup: {},
      mainGroup: {},
    };

  const { getData } = useDataFields(newData);

  return {
    titleGroup: {
      title: getData("titleGroup.title")?.text,
      isShowTitle: getData("titleGroup.isShown")?.value,
      spacing: getData("titleGroup.spacing")?.value,
    },
    contentGroup: {
      text: getData("contentGroup.content")?.text,
      isShowOverlay: getData("contentGroup.isShowOverlay")?.value,
      backgroundColor: getData("contentGroup.backgroundColor")?.value,
      borderRadius: getData("contentGroup.borderRadius")?.value,
      blur: getData("contentGroup.blur")?.value,
      shadow: getData("contentGroup.shadow"),
      paddings: applyDefaultContentPaddings(getData("contentGroup.paddings")),
    },
    mainGroup: {
      isShowMedia: getData("mainGroup.isShowMedia")?.value,
      type: getData("mainGroup.media")?.type,
      objectFit: getData("mainGroup.media")?.objectFit ?? 'cover',
      imageProps: CssImageHelpers.imageProps(getData("mainGroup.media")),
      videoProps: CssVideoHelpers.videoProps(getData("mainGroup.media")),
      maskProps: getData("mainGroup.mediaMask"),
      color: getData("mainGroup.color")?.value,
      isShowColor: getData("mainGroup.isShowColor")?.value,
      borderRadius: getData("mainGroup.borderRadius")?.value,
      isShowPattern: getData("mainGroup.isShowPattern")?.value,
      pattern: getData("mainGroup.pattern"),
      paddings: applyDefaultMainPaddings(getData("mainGroup.paddings")),
      margins: applyDefaultMainMargins(getData("mainGroup.margins")),
    },
  };
});

const isEditMode = computed(() => props.mode === "edit");

// Перевіряємо чи блок пустий
const isEmptyBlock = computed(() => {
  const hasTitle = fields.value?.titleGroup?.title && fields.value?.titleGroup?.title?.trim() !== '';
  const hasContent = fields.value?.contentGroup?.text && fields.value?.contentGroup?.text?.trim() !== '';
  const hasMedia = fields.value?.mainGroup?.isShowMedia && (
    fields.value?.mainGroup?.imageProps?.imageSrc || 
    fields.value?.mainGroup?.videoProps?.videoSrc
  );
  const hasPattern = fields.value?.mainGroup?.isShowPattern;
  const hasColor = fields.value?.mainGroup?.isShowColor && fields.value?.mainGroup?.color;
  
  return !(hasTitle || hasContent || hasMedia || hasPattern || hasColor);
});

// Убираем onMounted и watch, так как теперь используем данные напрямую
</script>
<style scoped>
@reference "tailwindcss";
/* Основные стили для контейнера */
.lesson-text-box {
  @apply w-full flex flex-col gap-0;
  container-type: inline-size;
}

/* Стили для заголовка */
.lesson-title {
  @apply mb-4 relative z-10;
  color: var(--text-primary);
  border-radius: 0.5rem;
  /* display: inline-block; */
}

/* Стили для текста */
.lesson-text {
  @apply text-base leading-relaxed m-0 p-0 mb-6;
  color: var(--text-primary);
}

/* Стили для медиа фона */
.media-background {
  @apply w-full h-full absolute inset-0 z-0;
  border-radius: inherit;
  overflow: hidden;
}

/* Стили для патерну через псевдоелемент з реальними розмірами */
.pattern-background::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: var(--pattern-url);
  background-repeat: repeat;
  background-size: var(--pattern-width) var(--pattern-height);
  opacity: var(--pattern-opacity);
  z-index: 0;
  pointer-events: none;
}

.media-background :deep(.media-card) {
  height: 100%;
}

/* Override @container height:auto !important from MediaContainerLayer and MaskLayer —
   those resets are correct for inline media but must not apply to the background fill. */
.media-background :deep(.media-container-layer),
.media-background :deep(.media-container-layer > div) {
  height: 100% !important;
  min-height: unset !important;
  max-height: unset !important;
  aspect-ratio: unset !important;
}

.media-background :deep(img),
.media-background :deep(video) {
  @apply w-full h-full object-cover;
}

/* Стили для текста поверх медиа */
.lesson-text-overlay {
  @apply text-base leading-relaxed m-0 p-0 relative z-10;
  /* color: var(--text-primary); */
  /* background: rgba(255, 255, 255, 0.2); */
}

/* Адаптивность для мобильных устройств */
@container (max-width: 768px) {
  .lesson-text {
    @apply text-base;
  }

  .lesson-title {
    @apply text-base;
  }
}
</style>
<style>
@import "../styles/dialogs-styles.css";
</style>
